import type { TranscriptItem } from "@elation/api-client/src/stitch-web/models/transcriptItem";
import { TranscriptItemState } from "@elation/api-client/src/stitch-web/models/transcriptItemState";

import { humanizeIdentifier } from "@src/shared/utils/strings";

type MaybeTranscriptState = Pick<TranscriptItem, "state"> | null | undefined;

const STATE_LABELS: Record<TranscriptItem["state"], string> = {
  [TranscriptItemState.not_sent]: "Draft",
  [TranscriptItemState.sent]: "Sent",
  [TranscriptItemState.processed]: "Processed",
  [TranscriptItemState.errored]: "Errored",
};

type SpeakerKeyedItem = Pick<
  TranscriptItem,
  "speaker" | "startOffsetMs" | "endOffsetMs" | "id"
>;

function speakerLabelFromValue(value: string | null | undefined): string {
  const normalized = value?.trim();
  if (!normalized) return "Unknown speaker";

  const lower = normalized.toLowerCase();
  if (lower === "doctor" || lower === "clinician" || lower === "provider") {
    return "Clinician";
  }

  if (lower === "patient") return "Patient";

  return humanizeIdentifier(normalized);
}

export const TranscriptItemEntity = {
  speakerLabel(
    item: Pick<TranscriptItem, "speaker"> | null | undefined,
  ): string | null {
    if (!item) return null;
    return speakerLabelFromValue(item?.speaker);
  },

  buildSessionSpeakerLabelMap(
    items: SpeakerKeyedItem[],
  ): ReadonlyMap<string, string> {
    const map = new Map<string, string>();
    let nextSpeakerNumber = 1;

    for (const item of [...items].sort(
      TranscriptItemEntity.compareByOffsetAsc,
    )) {
      const speakerId = item.speaker?.trim();
      if (!speakerId || map.has(speakerId)) continue;

      map.set(speakerId, `Speaker ${nextSpeakerNumber}`);
      nextSpeakerNumber += 1;
    }

    return map;
  },

  sessionSpeakerLabel(
    speaker: string | null | undefined,
    labelMap: ReadonlyMap<string, string>,
  ): string | null {
    const normalized = speaker?.trim();
    if (!normalized) return null;
    return labelMap.get(normalized) ?? null;
  },

  stateLabel(item: MaybeTranscriptState): string | null {
    if (!item) return null;
    return STATE_LABELS[item.state];
  },

  isFinalized(item: MaybeTranscriptState): boolean {
    if (!item) return false;
    return item.state === TranscriptItemState.processed;
  },

  isDraft(item: MaybeTranscriptState): boolean {
    if (!item) return false;
    return (
      item.state === TranscriptItemState.not_sent ||
      item.state === TranscriptItemState.sent
    );
  },

  isErrored(item: MaybeTranscriptState): boolean {
    if (!item) return false;
    return item.state === TranscriptItemState.errored;
  },

  isDeleted(
    item: Pick<TranscriptItem, "deletedAt"> | null | undefined,
  ): boolean {
    return Boolean(item?.deletedAt);
  },

  isEmptyText(item: Pick<TranscriptItem, "text"> | null | undefined): boolean {
    return !item?.text?.trim();
  },

  durationMs(
    item:
      | Pick<TranscriptItem, "startOffsetMs" | "endOffsetMs">
      | null
      | undefined,
  ): number {
    if (!item) return 0;
    return Math.max(0, item.endOffsetMs - item.startOffsetMs);
  },

  compareByOffsetAsc(left: SpeakerKeyedItem, right: SpeakerKeyedItem): number {
    const byStart = left.startOffsetMs - right.startOffsetMs;
    if (byStart !== 0) return byStart;

    const byEnd = left.endOffsetMs - right.endOffsetMs;
    if (byEnd !== 0) return byEnd;

    return left.id - right.id;
  },
} as const;
